import React, { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faPause, faVolumeHigh, faSliders, faCog } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { getApiUrl } from '../../config/api';
import { AudioTrack } from './types';
import { loadAuthenticatedAudioBuffer } from '../../utils/audioProxyUtil';
import { downloadAudio } from '../../services/autoDownloadService';
import styles from './AudioModal.module.css';
import { 
  audioBufferToMp3, 
  audioBufferToWav, 
  calculateExportFileSize,
  getMp3QualityDescription,
  getMp3QualityName,
  Mp3QualityLevel 
} from '../../utils/audio';

interface AudioMixerProps {
  tracks: AudioTrack[];
  totalDuration: number;
  masterVolume: number;
  isMasterMuted: boolean;
  isVisible: boolean;
  onClose: () => void;
  onMixComplete?: (audioUrl: string) => void;
  projectId?: string;
}

export const AudioMixer: React.FC<AudioMixerProps> = ({
  tracks,
  totalDuration,
  masterVolume,
  isMasterMuted,
  isVisible,
  onClose,
  onMixComplete,
  projectId
}) => {
  const { t } = useTranslation('audioEditor');
  
  console.log('🎛️ AudioMixer rendered:', { 
    tracks: tracks.length, 
    isVisible, 
    totalDuration 
  });

  const { showNotification } = useNotification();
  const { token } = useAuth();
  const [isMixing, setIsMixing] = useState(false);
  const [exportFormat, setExportFormat] = useState<'mp3' | 'wav'>('mp3');
  const [mp3Quality, setMp3Quality] = useState<Mp3QualityLevel>(128);
  const [wavCompression, setWavCompression] = useState<boolean>(false);

  // Log all track details when modal loads
  React.useEffect(() => {
    if (isVisible && tracks.length > 0) {
      console.log('🎵 MODAL LOADED - Current Track States:');
      tracks.forEach((track, index) => {
        console.log(`Track ${index + 1}:`, {
          id: track.id,
          name: track.name,
          volume: track.volume,
          muted: track.muted || track.isMuted, // Handle both property names
          isMuted: track.isMuted,
          type: track.type,
          duration: track.duration,
          startTime: track.startTime
        });
      });
      console.log('🎛️ Master Settings:', {
        masterVolume,
        isMasterMuted
      });
    }
  }, [isVisible, tracks, masterVolume, isMasterMuted]);

  const handleMix = async () => {
    console.log('🚀 Merging audio tracks in browser...');
    setIsMixing(true);
    
    try {
      if (tracks.length === 0) {
        throw new Error('No tracks to merge');
      }

      // Create audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const sampleRate = audioContext.sampleRate;
      
      // Calculate the actual duration needed based ONLY on trimmed track end points
      // (ignore totalDuration as it may still reflect original untrimmed duration)
      const trackEndPoints = tracks.map(track => {
        const trimmedDuration = (track.trimEnd || track.duration || 0) - (track.trimStart || 0);
        return (track.startTime || 0) + trimmedDuration;
      });
      
      const maxDuration = trackEndPoints.length > 0 ? Math.max(...trackEndPoints) : totalDuration;
      const totalSamples = Math.ceil(maxDuration * sampleRate);
      const numberOfChannels = 2; // Stereo
      
      // Create output buffer
      const mergedBuffer = audioContext.createBuffer(numberOfChannels, totalSamples, sampleRate);
      const leftChannel = mergedBuffer.getChannelData(0);
      const rightChannel = mergedBuffer.getChannelData(1);
      
      console.log(`🎵 Buffer Duration Calculation:`, {
        originalTotalDuration: totalDuration.toFixed(2) + 's',
        calculatedFromTracks: maxDuration.toFixed(2) + 's',
        usingDuration: maxDuration.toFixed(2) + 's'
      });
      console.log(`🎵 Merging ${tracks.length} tracks into ${maxDuration.toFixed(2)}s buffer`);
      
      // Log track end points for debugging
      console.log('📊 Track End Points:', tracks.map(track => {
        const trimmedDuration = (track.trimEnd || track.duration || 0) - (track.trimStart || 0);
        const endPoint = (track.startTime || 0) + trimmedDuration;
        return {
          name: track.name || track.id,
          startTime: track.startTime || 0,
          trimStart: track.trimStart || 0,
          trimEnd: track.trimEnd || track.duration || 0,
          originalDuration: track.duration || 0,
          trimmedDuration: trimmedDuration.toFixed(2) + 's',
          endPoint: endPoint.toFixed(2) + 's'
        };
      }));
      
      console.log('🎵 Starting audio merge with tracks:', tracks.map(t => ({
        id: t.id,
        name: t.name,
        volume: t.volume,
        muted: t.muted,
        type: t.type
      })));
      
      // Load and merge each track
      for (const track of tracks) {
        console.log(`🎛️ Track Settings:`, {
          name: track.name || track.id,
          trackVolume: track.volume,
          trackMuted: track.muted,
          masterVolume: masterVolume,
          isMasterMuted: isMasterMuted
        });
        
        // Check if track should be skipped (handle both muted and isMuted properties)
        const isMuted = track.muted === true || track.isMuted === true;
        const hasZeroVolume = (track.volume === 0);
        const shouldSkip = isMuted || hasZeroVolume;
        
        console.log(`🔍 Should skip track "${track.name || track.id}"?`, shouldSkip, {
          muted: track.muted,
          isMuted: track.isMuted,
          isMutedResult: isMuted,
          volume: track.volume,
          hasZeroVolume: hasZeroVolume
        });
        
        if (shouldSkip) {
          console.log(`⏭️ Skipping track: ${track.name || track.id} (muted: ${isMuted}, zero volume: ${hasZeroVolume})`);
          continue;
        }
        
        try {
          console.log(`🎧 Processing track: ${track.name || track.id}`);
          
          // Use proxy to load audio data and avoid CORS issues
          console.log(`🎵 Loading audio buffer via proxy for track: ${track.name}, URL: ${track.url}`);
          const audioBuffer = await loadAuthenticatedAudioBuffer(track.url, audioContext);
          
          // Calculate track parameters with trim support
          const trackVolume = (track.volume || 1) * (isMasterMuted ? 0 : masterVolume);
          const startSample = Math.floor((track.startTime || 0) * sampleRate);
          
          // Respect trim settings
          const trimStartSeconds = track.trimStart || 0;
          const trimEndSeconds = track.trimEnd || audioBuffer.duration;
          const trimStartSample = Math.floor(trimStartSeconds * sampleRate);
          const trimEndSample = Math.floor(trimEndSeconds * sampleRate);
          const effectiveTrackSamples = Math.min(trimEndSample - trimStartSample, audioBuffer.length - trimStartSample);
          
          console.log(`🔊 Final Volume Calculation:`, {
            trackName: track.name || track.id,
            originalVolume: track.volume,
            masterVolume: masterVolume,
            isMasterMuted: isMasterMuted,
            finalTrackVolume: trackVolume,
            willBeAudible: trackVolume > 0,
            trimInfo: {
              trimStart: trimStartSeconds,
              trimEnd: trimEndSeconds,
              originalDuration: audioBuffer.duration,
              effectiveDuration: (trimEndSeconds - trimStartSeconds).toFixed(2) + 's',
              effectiveSamples: effectiveTrackSamples
            }
          });
          
          // Mix the track into the output buffer (respecting trim settings)
          const inputChannels = audioBuffer.numberOfChannels;
          
          if (inputChannels === 1) {
            // Mono input - mix to both left and right channels
            const inputData = audioBuffer.getChannelData(0);
            
            for (let i = 0; i < effectiveTrackSamples && (startSample + i) < totalSamples; i++) {
              const inputIndex = trimStartSample + i;
              const outputIndex = startSample + i;
              
              if (outputIndex >= 0 && inputIndex < audioBuffer.length) {
                let sample = inputData[inputIndex] * trackVolume;
                
                // Apply fade in/out if specified
                const fadeInSamples = Math.floor((track.fadeInDuration || 0) * sampleRate);
                const fadeOutSamples = Math.floor((track.fadeOutDuration || 0) * sampleRate);
                
                if (fadeInSamples > 0 && i < fadeInSamples) {
                  sample *= (i / fadeInSamples); // Fade in
                }
                
                if (fadeOutSamples > 0 && i >= (effectiveTrackSamples - fadeOutSamples)) {
                  const fadeOutProgress = (effectiveTrackSamples - i) / fadeOutSamples;
                  sample *= fadeOutProgress; // Fade out
                }
                
                leftChannel[outputIndex] += sample;
                rightChannel[outputIndex] += sample;
              }
            }
          } else {
            // Stereo or multi-channel input
            for (let channel = 0; channel < Math.min(numberOfChannels, inputChannels); channel++) {
              const inputData = audioBuffer.getChannelData(channel);
              const outputData = channel === 0 ? leftChannel : rightChannel;
              
              for (let i = 0; i < effectiveTrackSamples && (startSample + i) < totalSamples; i++) {
                const inputIndex = trimStartSample + i;
                const outputIndex = startSample + i;
                
                if (outputIndex >= 0 && inputIndex < audioBuffer.length) {
                  let sample = inputData[inputIndex] * trackVolume;
                  
                  // Apply fade in/out if specified
                  const fadeInSamples = Math.floor((track.fadeInDuration || 0) * sampleRate);
                  const fadeOutSamples = Math.floor((track.fadeOutDuration || 0) * sampleRate);
                  
                  if (fadeInSamples > 0 && i < fadeInSamples) {
                    sample *= (i / fadeInSamples); // Fade in
                  }
                  
                  if (fadeOutSamples > 0 && i >= (effectiveTrackSamples - fadeOutSamples)) {
                    const fadeOutProgress = (effectiveTrackSamples - i) / fadeOutSamples;
                    sample *= fadeOutProgress; // Fade out
                  }
                  
                  outputData[outputIndex] += sample;
                }
              }
            }
          }
          
          console.log(`✅ Successfully mixed: ${track.name || track.id}`);
          
        } catch (trackError) {
          console.error(`❌ Failed to process track ${track.name || track.id}:`, trackError);
          // Continue with other tracks
        }
      }
      
      // Convert to selected format blob
      let audioBlob: Blob;
      let fileExtension: string;
      
      try {
        if (exportFormat === 'mp3') {
          audioBlob = await audioBufferToMp3(mergedBuffer, mp3Quality);
          fileExtension = 'wav'; // Actually WAV with compression
        } else {
          audioBlob = audioBufferToWav(mergedBuffer, wavCompression);
          fileExtension = 'wav';
        }
      } catch (conversionError) {
        console.error('❌ Audio conversion failed:', conversionError);
        
        // Fallback to compressed WAV if MP3 encoding fails
        console.log('🔄 Falling back to compressed WAV...');
        audioBlob = audioBufferToWav(mergedBuffer, true);
        fileExtension = 'wav';
        
        showNotification({
          title: 'Format Changed',
          message: 'MP3 encoding failed, exported as compressed WAV instead',
          type: 'warning'
        });
      }
      
      // Save to backend and get S3 URL
      console.log('💾 Saving merged audio to backend...');
      const s3Url = await saveMergedAudioToBackend(audioBlob, projectId, fileExtension);
      
      if (s3Url) {
        // Auto-download the mixed audio file
        try {
          await downloadAudio(s3Url, {
            filename: `mixed-audio-${Date.now()}.${fileExtension}`,
            setIsProcessing: undefined // We manage loading state separately
          });
          
          // showNotification({
          //   title: 'Audio Merged & Downloaded!',
          //   message: `Combined ${tracks.length} tracks and automatically downloaded`,
          //   type: 'success'
          // });
        } catch (downloadError) {
          console.error('❌ Auto-download failed:', downloadError);
          // Still show success for mixing, but note download issue
          // showNotification({
          //   title: 'Audio Merged Successfully!',
          //   message: `Combined ${tracks.length} tracks and saved. Download failed - use Export Mix button.`,
          //   type: 'success'
          // });
        }
        
        // Notify parent component that mix is complete with the S3 URL
        if (onMixComplete) {
          onMixComplete(s3Url);
        }
        
        // Close the modal after successful save
        onClose();
      } else {
        // Fallback: create local blob URL for download if backend save failed
        const downloadUrl = URL.createObjectURL(audioBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `merged-audio-${Date.now()}.${fileExtension}`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);
        
        // showNotification({
        //   title: 'Audio Merged Successfully!',
        //   message: `Combined ${tracks.length} tracks (saved locally only)`,
        //   type: 'success'
        // });
        
        // Close the modal even for local save
        onClose();
      }
      
      // Clean up
      audioContext.close();
      
    } catch (error) {
      console.error('❌ Audio merge failed:', error);
      showNotification({
        title: 'Merge Failed',
        message: error instanceof Error ? error.message : 'Failed to merge audio tracks',
        type: 'error'
      });
    } finally {
      setIsMixing(false);
    }
  };

  // Helper function to save merged audio to backend and update project
  const saveMergedAudioToBackend = async (audioBlob: Blob, projectId?: string, fileExt: string = 'wav'): Promise<string | null> => {
    try {
      console.log('🚀 Uploading merged audio to backend and updating project...');
      
      const formData = new FormData();
      formData.append('audio', audioBlob, `merged-audio-${Date.now()}.${fileExt}`);
      
      if (projectId) {
        formData.append('projectId', projectId);
      }
      
      // Add comprehensive metadata about the merge operation
      // formData.append('metadata', JSON.stringify({
      //   type: 'final-merged-audio',
      //   tracksCount: tracks.length.toString(),
      //   totalDuration: totalDuration.toString(),
      //   masterVolume: masterVolume.toString(),
      //   isMasterMuted: isMasterMuted.toString(),
      //   mergedAt: new Date().toISOString(),
      //   trackDetails: JSON.stringify(tracks.map(track => ({
      //     id: track.id,
      //     name: track.name,
      //     volume: track.volume.toString(),
      //     muted: (track.isMuted || track.muted || false).toString(),
      //     duration: track.duration.toString(),
      //     startTime: track.startTime.toString(),
      //     type: track.type || 'audio',
      //     url: track.url
      //   }))),
      //   // Audio mixing settings
      //   sampleRate: '44100',
      //   channels: '2',
      //   format: 'WAV',
      //   bitDepth: '16'
      // }));
      
      const response = await fetch(getApiUrl('/music-generation/save-merged'), {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data?.s3Url) {
        console.log('✅ Merged audio saved to S3:', result.data.s3Url);
        
        // Verify project was updated with finalAudio field
        if (projectId && result.data.projectUpdated) {
          console.log('📝 Project updated with finalAudio URL:', result.data.s3Url);
          console.log('🎯 Project finalAudio field set to:', result.data.s3Url);
        }
        
        return result.data.s3Url;
      } else {
        console.error('❌ Backend save failed:', result.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Error saving merged audio to backend:', error);
      return null;
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FontAwesomeIcon icon={faSliders} style={{ marginRight: '12px' }} />
            {t('mixer.title')}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div style={{ padding: '32px' }}>
          {/* Track Information */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ 
                color: 'white', 
                margin: '0 0 16px 0',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faVolumeHigh} />
                {t('mixer.overview.title')}
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                color: '#a0a0a0'
              }}>
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.audioTracks')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#3b82f6', fontWeight: '600' }}>
                    {tracks.length}
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.totalDuration')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#8b5cf6', fontWeight: '600' }}>
                    {totalDuration.toFixed(1)}s
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.masterVolume')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#22c55e', fontWeight: '600' }}>
                    {(masterVolume * 100).toFixed(0)}%
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.status')}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: isMasterMuted ? '#ef4444' : '#22c55e',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {isMasterMuted ? t('mixer.overview.muted') : t('mixer.overview.active')}
                  </div>
                </div>
              </div>
            </div>

            {/* Track List */}
            {tracks.length > 0 && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <h3 style={{ 
                  color: 'white', 
                  margin: '0 0 16px 0',
                  fontSize: '18px'
                }}>
                  {t('mixer.trackDetails.title')}
                </h3>
                
                <div style={{ 
                  display: 'grid',
                  gap: '12px'
                }}>
                  {tracks.map((track, index) => (
                    <div key={track.id} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          color: 'white',
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          {track.name || `Track ${index + 1}`}
                        </div>
                        <div style={{ 
                          color: '#a0a0a0',
                          fontSize: '14px',
                          display: 'flex',
                          gap: '16px'
                        }}>
                          <span>{t('mixer.trackDetails.type')} {track.type}</span>
                          <span>{t('mixer.trackDetails.volume')} {track.volume === 0 ? '0' : (track.volume * 100).toFixed(0)}%</span>
                          {track.duration && <span>{t('mixer.trackDetails.duration')} {track.duration.toFixed(1)}s</span>}
                        </div>
                      </div>
                      
                      <div style={{ 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: (track.muted === true || track.isMuted === true || track.volume === 0) ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        color: (track.muted === true || track.isMuted === true || track.volume === 0) ? '#ef4444' : '#22c55e'
                      }}>
                        {(track.muted === true || track.isMuted === true || track.volume === 0) ? t('mixer.overview.muted') : t('mixer.overview.active')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Export Settings */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ 
              color: 'white', 
              margin: '0 0 20px 0',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FontAwesomeIcon icon={faCog} />
              {t('mixer.exportSettings.title')}
            </h3>
            
            {/* Format Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block',
                color: 'white',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {t('mixer.exportSettings.format')}
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setExportFormat('mp3')}
                  style={{
                    background: exportFormat === 'mp3' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {t('mixer.exportSettings.formatCompressed')}
                </button>
                <button
                  onClick={() => setExportFormat('wav')}
                  style={{
                    background: exportFormat === 'wav' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {t('mixer.exportSettings.formatFull')}
                </button>
              </div>
            </div>

            {/* Compression Quality Settings */}
            {exportFormat === 'mp3' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block',
                  color: 'white',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Compression Level (Quality: {getMp3QualityName(mp3Quality)})
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  {[96, 128, 192, 320].map((quality) => (
                    <button
                      key={quality}
                      onClick={() => setMp3Quality(quality as Mp3QualityLevel)}
                      style={{
                        background: mp3Quality === quality ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Level {quality === 96 ? '4' : quality === 128 ? '3' : quality === 192 ? '2' : '1'}
                    </button>
                  ))}
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '12px' }}>
                  {getMp3QualityDescription(mp3Quality)}
                </div>
              </div>
            )}

            {/* WAV Compression Option */}
            {exportFormat === 'wav' && (
              <div>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  cursor: 'pointer',
                  gap: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={wavCompression}
                    onChange={(e) => setWavCompression(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Use 8-bit compression (smaller file size)
                </label>
                <div style={{ color: '#a0a0a0', fontSize: '12px', marginTop: '4px', marginLeft: '20px' }}>
                  {wavCompression 
                    ? 'Reduces file size by ~50% with slight quality loss'
                    : `Full 16-bit quality (~${Math.round(totalDuration * 10.5)}MB estimated)`
                  }
                </div>
              </div>
            )}

            {/* File Size Estimate */}
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px'
            }}>
              <div style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                {t('mixer.exportSettings.estimatedSize')}
              </div>
              <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                {(() => {
                  const sizeMB = calculateExportFileSize(
                    totalDuration,
                    exportFormat,
                    mp3Quality,
                    wavCompression
                  );
                  return t('mixer.exportSettings.approximateSize', { size: sizeMB });
                })()}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button onClick={onClose} className={styles.cancelButton}>
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            {t('mixer.actions.cancel')}
          </button>
          <button 
            onClick={handleMix}
            disabled={isMixing || tracks.length === 0}
            className={styles.primaryButton}
            style={{ 
              opacity: isMixing || tracks.length === 0 ? 0.6 : 1,
              cursor: isMixing || tracks.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            <FontAwesomeIcon icon={faCog} style={{ marginRight: '8px' }} />
            {isMixing 
              ? t('mixer.actions.exporting')
              : t('mixer.actions.export', { 
                  format: exportFormat === 'mp3' 
                    ? t('mixer.actions.exportCompressed') 
                    : t('mixer.actions.exportFull')
                })
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioMixer;
